import React, {useEffect, useState} from 'react';
import {AnswerService} from '../AnswerService';
import {getUrl} from 'aws-amplify/storage';
import SimpleLoader from '../../../components/SimpleLoader/SimpleLoader';
// @ts-ignore: TS2459 — we know it's there at runtime
import {AttendanceDashboardModal as AttendanceDashboardModalUI} from '@PikaDoApp/pikado-web-dsm';
export interface Question {
  id: string;
  numOfAns: number;
  answersIds: string[];
}
export interface InfoProps {
  average: number;
  numOfTotalAns: number;
  numOfTotalQuestions: number;
  numOfUniqueTotalAns: number;
  questions: Question[];
  totalScores: number;
  day: number;
  totalAnswers?: string[];
  performanceId: string;
}
interface QuestionData {
  index: number;
  numOfAns: number;
  answers: AnswerData[];
}
interface AnswerData {
  title: string;
  src: string;
  isAudio: boolean;
  progressScore: number;
  onAudioClick: () => void;
}

interface QuestionData {
  index: number;
  numOfAns: number;
  answers: AnswerData[];
}

interface StatsData {
  numOfTotalAns: number;
  numOfTotalQuestions: number;
  average: number;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  info: InfoProps;
}

const AttendanceDashboardModalContent: React.FC<Props> = ({
  isOpen,
  onClose,
  info,
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [questionsData, setQuestionsData] = useState<QuestionData[]>([]);
  const [currentPlayingId, setCurrentPlayingId] = useState<string | null>(null);

  const [statsData, setStatsData] = useState<StatsData>({
    numOfTotalAns: 0,
    numOfTotalQuestions: 0,
    average: 0
  });
  const answerService = new AnswerService();

  const handleAudioClick = (answerId: string) => {
    if (currentPlayingId === answerId) {
      setCurrentPlayingId(null); // Stop if clicking the same audio
    } else {
      setCurrentPlayingId(answerId); // Play the new audio
    }
  };

  const transformQuestionAnswerPairs = async (questionAnswerPairs: any[]): Promise<QuestionData[]> => {
    if (!questionAnswerPairs || questionAnswerPairs.length === 0) {
      return [];
    }

    try {
      const formattedQuestions: QuestionData[] = await Promise.all(
        questionAnswerPairs.map(async (pair, questionIndex) => {
          // Validate pair has required data
          if (!pair || !pair.answers || pair.answers.length === 0) {
            return null;
          }

          // Format answers for this question
          const formattedAnswers: AnswerData[] = await Promise.all(
            pair.answers.map(async (answer: any, answerIndex: number) => {
              // Validate answer data
              if (!answer || !answer.answerId) {
                return null;
              }

              let audioUrl = '';

              // Only fetch audio URL for MULTI_ANSWER type
              if (pair.answerAttemptType === 'MULTI_ANSWER') {
                try {
                  // Fetch the full answer details to get audioUri
                  const answerDetails = await answerService.getAnswer(answer.answerId);
                  const audioUri = answerDetails?.newResponse?.audioUri;

                  if (audioUri && audioUri !== 'undefined' && audioUri !== '') {
                    const uri = await getUrl({ path: 'public/' + audioUri });
                    audioUrl = uri.url?.href || '';
                  }
                } catch (error) {
                  console.error(`Failed to fetch audio for answer ${answer.answerId}:`, error);
                }
              }

              return {
                title: `Answer ${answerIndex + 1}`,
                src: pair.answerAttemptType === 'SINGLE_ANSWER' ? '' : audioUrl,
                isAudio: pair.answerAttemptType === 'MULTI_ANSWER' && !!audioUrl,
                progressScore: Number((answer.score || 0).toFixed(2)),
                onAudioClick: () => handleAudioClick(answer.answerId),
              };
            })
          );

          // Filter out any null answers
          const validAnswers = formattedAnswers.filter((a): a is AnswerData => a !== null);

          if (validAnswers.length === 0) {
            return null;
          }


          return {
            index: questionIndex,
            numOfAns: validAnswers.length,
            answers: validAnswers,
          };
        })
      );

      // Filter out any null questions and return
      return formattedQuestions.filter((q): q is QuestionData => q !== null);
    } catch (error) {
      console.error('Error transforming question answer pairs:', error);
      return [];
    }
  };

  const onCloseHandler = () => {
    setCurrentPlayingId(null);
    setIsLoading(true); // Reset loading state for next open
    setQuestionsData([]); // Clear data // Clear student performance data
    setStatsData({ numOfTotalAns: 0, numOfTotalQuestions: 0, average: 0 }); // Reset stats data
    onClose();
  };

  useEffect(() => {
    if (!isOpen || !info) {
      return;
    }

    setIsLoading(false);
  }, [isOpen, info]);

  useEffect(() => {
    if (!isOpen || !info?.performanceId) return;

    const fetchDailyPerformance = async () => {
      setIsLoading(true);
      try {
        const performance = await answerService.getDailyPerformanceById(info.performanceId);
        // Set stats data
        const data = {
          numOfTotalAns: performance.dailyAnswersCount,
          numOfTotalQuestions: performance.dailyUniqueAnswersCount,
          average: performance.dailyExercisesAverage
        };
        setStatsData(data);

        // Transform and set questions data
        if (performance.questionAnswerPairs) {
          const transformedQuestions = await transformQuestionAnswerPairs(performance.questionAnswerPairs);

          setQuestionsData(transformedQuestions);
        }
      } catch (error) {
        console.error('Error fetching daily performance:', error);
        setStatsData({ numOfTotalAns: 0, numOfTotalQuestions: 0, average: 0 });
        setQuestionsData([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchDailyPerformance();
  }, [isOpen, info?.performanceId]);

  // Cleanup on component unmount
  useEffect(() => {
    return () => {
      setCurrentPlayingId(null);
      setQuestionsData([]);
      setStatsData({ numOfTotalAns: 0, numOfTotalQuestions: 0, average: 0 });
    };
  }, []);

  if (!isOpen) return null;

  // Show loading state
  if (isLoading) {
    return (
      <SimpleLoader
        isLoading={true}
        title="Loading Performance Data..."
        subtitle="Please wait while we fetch your answers"
      />
    );
  }
  const onAudioPlay = async()=>{
    console.log("Test")
  }
  return (
    <AttendanceDashboardModalUI
      open={isOpen}
      onClose={onCloseHandler}
      title={`Day ${info.day} Performance`}
      statsData={statsData}
      questionsData={questionsData}
    />
  );
};

export default AttendanceDashboardModalContent;
